import React, { useEffect, useMemo, useState } from "react";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border";
  const styles = {
    default: "bg-gray-100 text-gray-800 border-gray-200",
    green: "bg-green-100 text-green-800 border-green-200",
    yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    red: "bg-red-100 text-red-800 border-red-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children}
    </span>
  );
};

const Button = ({
  children,
  onClick,
  disabled,
  variant = "primary",
  title,
}) => {
  const base =
    "inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition border";
  const styles = {
    primary:
      "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:border-gray-300",
    secondary:
      "bg-white text-black border-gray-300 hover:bg-gray-50 disabled:opacity-60",
    danger:
      "bg-white text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-60",
    success:
      "bg-green-600 text-white border-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:border-gray-300",
  };

  return (
    <button
      className={`${base} ${styles[variant] || styles.primary}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      type="button"
    >
      {children}
    </button>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  } catch {
    return String(iso);
  }
};

const safeStr = (v) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toCsv = (rows, delimiter = ";") => {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);

    if (
      s.includes('"') ||
      s.includes("\n") ||
      s.includes("\r") ||
      s.includes(delimiter)
    ) {
      return `"${s.replace(/"/g, '""')}"`;
    }

    return s;
  };

  return rows.map((r) => r.map(escape).join(delimiter)).join("\n");
};

const downloadTextFile = (
  filename,
  text,
  mime = "text/csv;charset=utf-8"
) => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

const formatIbanShort = (iban) => {
  if (!iban) return "Fehlt";
  const clean = String(iban).replace(/\s+/g, "");
  if (clean.length <= 8) return clean;
  return `${clean.slice(0, 4)} •••• ${clean.slice(-4)}`;
};

const getDisplayStatus = (item) => {
  if (item?.exported_at) return "exported";
  return String(item?.status || "new").toLowerCase();
};

const formatCurrency = (amount, currency = "EUR") => {
  const numeric = Number(amount || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currency || "EUR"}`;
  }
};

const resolveAdminCompanyId = (...values) => {
  for (const value of values) {
    const v = String(value || "").trim();
    if (v) return v;
  }

  const fromStorage = String(localStorage.getItem("company_id") || "").trim();
  if (fromStorage) return fromStorage;

  try {
    const rawContext = localStorage.getItem("company_context");
    if (rawContext) {
      const parsed = JSON.parse(rawContext);
      const contextCompanyId = String(parsed?.company_id || "").trim();
      if (contextCompanyId) return contextCompanyId;
    }
  } catch {
    // ignore invalid JSON
  }

  return "";
};

const buildJsonHeaders = ({ adminToken, companyId } = {}) => {
  const resolvedCompanyId = resolveAdminCompanyId(companyId);

  return {
    "Content-Type": "application/json",
    ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
    ...(resolvedCompanyId ? { "x-company-id": resolvedCompanyId } : {}),
  };
};

const readCsvFileAsText = async (file) => {
  const buffer = await file.arrayBuffer();

  try {
    return new TextDecoder("windows-1252", { fatal: false }).decode(buffer);
  } catch {
    try {
      return new TextDecoder("iso-8859-1", { fatal: false }).decode(buffer);
    } catch {
      try {
        return new TextDecoder("utf-8", { fatal: false }).decode(buffer);
      } catch {
        return new TextDecoder().decode(buffer);
      }
    }
  }
};

export default function AdminDashboard() {
  const [view, setView] = useState("cases"); // cases | tenants | mandantImport

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyCaseId, setBusyCaseId] = useState("");
  const [err, setErr] = useState("");

  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsErr, setTenantsErr] = useState("");
  const [copiedIbanFor, setCopiedIbanFor] = useState("");

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [onlyAcceptedForExport, setOnlyAcceptedForExport] = useState(true);

  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [importErr, setImportErr] = useState("");
  const [importResult, setImportResult] = useState(null);

  const adminToken =
    localStorage.getItem("admin_token") || localStorage.getItem("token") || "";

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("token");
    localStorage.removeItem("company_id");
    localStorage.removeItem("company_name");
    localStorage.removeItem("company_profile_completed");
    localStorage.removeItem("company_context");
    localStorage.removeItem("return_to");
    localStorage.removeItem("connect_mode");
    localStorage.removeItem("space_token");
    localStorage.removeItem("space_id");
    localStorage.removeItem("SpaceID");
    localStorage.removeItem("provider_token");
    localStorage.removeItem("provider_type");
    localStorage.removeItem("provider");
    localStorage.removeItem("provider_context_id");
    localStorage.removeItem("connected_provider_cobot");
    localStorage.removeItem("connected_provider_qonto");
    localStorage.removeItem("continue_without_provider");
    window.location.href = "/";
  };

  const loadInkasso = async () => {
    setLoading(true);
    setErr("");

    try {
      const effectiveCompanyId = resolveAdminCompanyId(selectedCompanyId);

      const res = await fetch(`${API_BASE}/inkasso`, {
        method: "GET",
        headers: buildJsonHeaders({
          adminToken,
          companyId: effectiveCompanyId,
        }),
      });

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`GET /inkasso failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : [];
      const list = Array.isArray(data) ? data : data?.items || [];

      list.sort((a, b) =>
        String(b?.created_at || b?.submitted_at || "").localeCompare(
          String(a?.created_at || a?.submitted_at || "")
        )
      );

      setItems(list);
    } catch (e) {
      setItems([]);
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadMandants = async () => {
    setTenantsLoading(true);
    setTenantsErr("");

    try {
      const res = await fetch(`${API_BASE}/company/admin`, {
        method: "GET",
        headers: buildJsonHeaders({
          adminToken,
          companyId: selectedCompanyId,
        }),
      });

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`GET /company/admin failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : [];
      const list = Array.isArray(data)
        ? data
        : data?.items || data?.companies || data?.tenants || [];

      list.sort((a, b) =>
        String(
          a?.company_name ||
            a?.legal_name ||
            a?.space_name ||
            a?.company_id ||
            ""
        ).localeCompare(
          String(
            b?.company_name ||
              b?.legal_name ||
              b?.space_name ||
              b?.company_id ||
              ""
          )
        )
      );

      setTenants(list);

      setSelectedCompanyId((current) => {
        if (current) return current;
        return (
          list?.[0]?.company_id ||
          list?.[0]?.space_id ||
          list?.[0]?.company_name ||
          ""
        );
      });
    } catch (e) {
      setTenants([]);
      setTenantsErr(e?.message || String(e));
    } finally {
      setTenantsLoading(false);
    }
  };

  useEffect(() => {
    loadMandants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resolveAdminCompanyId(selectedCompanyId)) return;
    loadInkasso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const acceptCase = async (caseId) => {
    setBusyCaseId(caseId);
    setErr("");

    try {
      const currentCase = items.find(
        (it) => String(it?.case_id || "") === String(caseId)
      );
      const caseCompanyId = resolveAdminCompanyId(
        currentCase?.company_id,
        selectedCompanyId
      );

      const res = await fetch(
        `${API_BASE}/inkasso/${encodeURIComponent(caseId)}`,
        {
          method: "PATCH",
          headers: buildJsonHeaders({
            adminToken,
            companyId: caseCompanyId,
          }),
          body: JSON.stringify({ accept: true, accepted_by: "admin" }),
        }
      );

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`PATCH /inkasso/${caseId} failed: ${res.status} ${txt}`);
      }

      await loadInkasso();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusyCaseId("");
    }
  };

  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase();

    return items.filter((x) => {
      const displayStatus = getDisplayStatus(x);

      if (statusFilter !== "all" && displayStatus !== statusFilter) return false;
      if (!query) return true;

      const hay = [
        x?.company_id,
        x?.space_name,
        x?.space_id,
        x?.invoice_number,
        x?.invoice_id,
        x?.id,
        x?.debtor_name,
        x?.debtor_contact_name,
        x?.debtor_email,
        x?.debtor_company,
        x?.debtor_city,
        x?.debtor_street,
        x?.debtor_zip,
        x?.debtor_country,
        x?.status,
        x?.case_id,
        x?.exported_at ? "exported" : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(query);
    });
  }, [items, q, statusFilter]);

  const acceptAllNew = async () => {
    const newOnes = filteredItems.filter((x) => getDisplayStatus(x) === "new");

    if (newOnes.length === 0) {
      alert("Keine neuen Fälle zum Annehmen.");
      return;
    }

    if (!window.confirm(`Wirklich ${newOnes.length} Fälle annehmen?`)) return;

    setErr("");

    try {
      for (const c of newOnes) {
        const cid = c?.case_id;
        if (!cid) continue;
        await acceptCase(cid);
      }

      await loadInkasso();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const exportAccepted = async () => {
    const rows = filteredItems.filter((x) => {
      if (!onlyAcceptedForExport) return true;
      return getDisplayStatus(x) === "accepted";
    });

    if (rows.length === 0) {
      alert(
        onlyAcceptedForExport
          ? "Keine ACCEPTED Fälle zum Export."
          : "Keine Fälle zum Export."
      );
      return;
    }

    const header = [
      "created_at",
      "company_id",
      "space_name",
      "space_id",
      "invoice_number",
      "invoice_id",
      "debtor_name",
      "debtor_contact_name",
      "debtor_email",
      "debtor_company",
      "debtor_street",
      "debtor_zip",
      "debtor_city",
      "debtor_country",
      "amount",
      "currency",
      "due_date",
      "cobot_reminder_level",
      "status",
      "exported_at",
      "case_id",
    ];

    const dataRows = rows.map((x) => [
      x?.created_at || x?.submitted_at || "",
      x?.company_id || "",
      x?.space_name || "",
      x?.space_id || "",
      x?.invoice_number || "",
      x?.invoice_id || x?.id || "",
      x?.debtor_name || "",
      x?.debtor_contact_name || "",
      x?.debtor_email || "",
      x?.debtor_company || "",
      x?.debtor_street || "",
      x?.debtor_zip || "",
      x?.debtor_city || "",
      x?.debtor_country || "",
      safeNum(x?.amount) ?? x?.amount ?? "",
      x?.currency || "",
      x?.due_date || "",
      x?.cobot_reminder_level ?? "",
      getDisplayStatus(x),
      x?.exported_at || "",
      x?.case_id || "",
    ]);

    const csv = toCsv([header, ...dataRows], ";");
    const fname = `inkasso_export_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadTextFile(fname, csv);
  };

  const countByStatus = useMemo(() => {
    const c = { all: items.length, new: 0, accepted: 0, exported: 0 };

    for (const it of items) {
      const displayStatus = getDisplayStatus(it);
      if (displayStatus === "exported") c.exported += 1;
      else if (displayStatus === "accepted") c.accepted += 1;
      else c.new += 1;
    }

    return c;
  }, [items]);

  const statusKind = (s) => {
    const v = (s || "new").toLowerCase();
    if (v === "accepted") return "green";
    if (v === "new") return "yellow";
    if (v === "exported") return "blue";
    if (v === "rejected") return "red";
    return "default";
  };

  const formatAddress = (tenant) => {
    const cityCountry = [
      tenant?.address_city || tenant?.debtor_city,
      tenant?.address_country || tenant?.debtor_country,
    ]
      .filter(Boolean)
      .join(", ");

    const streetZip = [
      tenant?.address_street || tenant?.debtor_street,
      tenant?.address_zip || tenant?.debtor_zip,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      line1: cityCountry || "—",
      line2: streetZip || "",
    };
  };

  const profileComplete = (tenant) => {
    return Boolean(
      (tenant?.legal_name || tenant?.company_name) &&
        tenant?.contact_email &&
        (tenant?.address_city || tenant?.debtor_city) &&
        (tenant?.address_country || tenant?.debtor_country) &&
        tenant?.iban
    );
  };

  const copyIban = async (tenant) => {
    const fullIban = tenant?.iban ? String(tenant.iban).replace(/\s+/g, "") : "";
    if (!fullIban) return;

    const copyKey =
      tenant?.company_id || tenant?.space_id || tenant?.space_name || "";

    try {
      await navigator.clipboard.writeText(fullIban);
      setCopiedIbanFor(copyKey);
      window.setTimeout(() => {
        setCopiedIbanFor("");
      }, 1500);
    } catch (e) {
      console.error("Clipboard copy failed", e);
      alert("Kopieren fehlgeschlagen.");
    }
  };

  const handleDeleteCases = async () => {
    setImportErr("");
    setImportResult(null);

    if (!selectedCompanyId) {
      setImportErr("Bitte zuerst einen Mandanten auswählen.");
      return;
    }

    const confirmed = window.confirm(
      `Wirklich den kompletten Bestand von "${selectedCompanyId}" löschen?`
    );
    if (!confirmed) return;

    try {
      setDeleteLoading(true);

      const res = await fetch(`${API_BASE}/admin/mandant-cases/delete-all`, {
        method: "POST",
        headers: buildJsonHeaders({
          adminToken,
          companyId: selectedCompanyId,
        }),
        body: JSON.stringify({
          company_id: selectedCompanyId,
        }),
      });

      const txt = await res.text();
      let data = null;

      try {
        data = txt ? JSON.parse(txt) : null;
      } catch {
        data = { raw: txt };
      }

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            data?.raw ||
            `Löschen fehlgeschlagen (${res.status})`
        );
      }

      setImportResult({
        success: true,
        action: "delete",
        imported_at: new Date().toISOString(),
        company_id: selectedCompanyId,
        deleted_previous_cases: data?.deleted ?? 0,
        deleted_cases: data?.deleted ?? 0,
        imported_cases: 0,
        received_cases: 0,
        skipped_closed_cases: 0,
        total_open_amount: 0,
        total_amount: 0,
        raw_response: data,
      });
    } catch (e) {
      setImportErr(e?.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleImport = async () => {
    setImportErr("");
    setImportResult(null);

    if (!selectedCompanyId) {
      setImportErr("Bitte zuerst einen Mandanten auswählen.");
      return;
    }

    if (!selectedFile) {
      setImportErr("Bitte zuerst eine Datei auswählen.");
      return;
    }

    try {
      setImportLoading(true);

      const csvText = await readCsvFileAsText(selectedFile);

      if (!csvText || !String(csvText).trim()) {
        throw new Error(
          "Die ausgewählte Datei ist leer oder konnte nicht gelesen werden."
        );
      }

      const res = await fetch(`${API_BASE}/admin/mandant-cases/import`, {
        method: "POST",
        headers: buildJsonHeaders({
          adminToken,
          companyId: selectedCompanyId,
        }),
        body: JSON.stringify({
          filename: selectedFile.name,
          csv_text: csvText,
        }),
      });

      const txt = await res.text();
      let data = null;

      try {
        data = txt ? JSON.parse(txt) : null;
      } catch {
        data = { raw: txt };
      }

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            data?.raw ||
            `Import fehlgeschlagen (${res.status})`
        );
      }

      setImportResult(data);
    } catch (e) {
      setImportErr(e?.message || "Import fehlgeschlagen.");
    } finally {
      setImportLoading(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setImportErr("");
    setImportResult(null);
    const fileInput = document.getElementById("mandant-case-import-file");
    if (fileInput) fileInput.value = "";
  };

  const importStats = useMemo(() => {
    if (!importResult) return null;

    return {
      importedAt:
        importResult?.imported_at ||
        importResult?.created_at ||
        new Date().toISOString(),
      receivedCases:
        importResult?.received_cases ??
        importResult?.record_count ??
        importResult?.imported_count ??
        importResult?.count ??
        0,
      importedCases:
        importResult?.imported_cases ??
        importResult?.record_count ??
        importResult?.imported_count ??
        importResult?.count ??
        0,
      skippedClosedCases: importResult?.skipped_closed_cases ?? 0,
      deletedPreviousCases:
        importResult?.deleted_previous_cases ??
        importResult?.deleted_cases ??
        0,
      totalOpenAmount:
        importResult?.total_open_amount ??
        importResult?.sum_offene_hf ??
        importResult?.offene_hf_sum ??
        importResult?.total_open_hf ??
        0,
      totalAmount:
        importResult?.sum_gesamt ??
        importResult?.gesamt_sum ??
        importResult?.total_amount ??
        importResult?.total_open_amount ??
        0,
      companyId: importResult?.company_id || selectedCompanyId || "—",
      importId: importResult?.import_id || "—",
      success:
        importResult?.success !== undefined ? Boolean(importResult.success) : true,
      action: importResult?.action || "import",
    };
  }, [importResult, selectedCompanyId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Admin Dashboard
            </h1>

            <div className="mt-2 text-sm text-gray-600 flex items-center gap-2 flex-wrap">
              <Badge kind="default">Alle: {countByStatus.all}</Badge>
              <Badge kind="yellow">New: {countByStatus.new}</Badge>
              <Badge kind="green">Accepted: {countByStatus.accepted}</Badge>
              <Badge kind="blue">Exported: {countByStatus.exported}</Badge>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              variant={view === "cases" ? "primary" : "secondary"}
              onClick={() => setView("cases")}
            >
              Übergaben
            </Button>

            <Button
              variant={view === "tenants" ? "primary" : "secondary"}
              onClick={() => setView("tenants")}
            >
              Mandanten
            </Button>

            <Button
              variant={view === "mandantImport" ? "primary" : "secondary"}
              onClick={() => setView("mandantImport")}
            >
              Aktenimport
            </Button>

            {view === "cases" ? (
              <>
                <Button
                  variant="secondary"
                  onClick={loadInkasso}
                  disabled={loading}
                >
                  {loading ? "…" : "Reload Übergaben"}
                </Button>

                <Button
                  variant="secondary"
                  onClick={acceptAllNew}
                  disabled={loading || filteredItems.length === 0}
                >
                  Alle neuen annehmen
                </Button>

                <Button
                  variant="success"
                  onClick={exportAccepted}
                  disabled={loading || filteredItems.length === 0}
                >
                  Export CSV
                </Button>
              </>
            ) : view === "tenants" ? (
              <Button
                variant="secondary"
                onClick={loadMandants}
                disabled={tenantsLoading}
              >
                {tenantsLoading ? "…" : "Reload Mandanten"}
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  loadMandants();
                  resetImport();
                }}
                disabled={tenantsLoading || importLoading || deleteLoading}
              >
                {tenantsLoading ? "…" : "Reload Importdaten"}
              </Button>
            )}

            <Button variant="danger" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {err && view === "cases" ? (
          <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
            {err}
          </div>
        ) : null}

        {tenantsErr && view === "tenants" ? (
          <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
            {tenantsErr}
          </div>
        ) : null}

        {importErr && view === "mandantImport" ? (
          <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
            {importErr}
          </div>
        ) : null}

        <div className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm">
          {view === "cases" ? (
            <>
              <div className="p-4 border-b border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      Status Filter
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white"
                    >
                      <option value="all">Alle</option>
                      <option value="new">New</option>
                      <option value="accepted">Accepted</option>
                      <option value="exported">Exported</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">Suche</div>
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Mandant, Rechnung, Schuldner, Firma, Case-ID..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    />
                  </div>

                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={onlyAcceptedForExport}
                        onChange={(e) =>
                          setOnlyAcceptedForExport(e.target.checked)
                        }
                      />
                      Nur <b>accepted</b> exportieren
                    </label>
                  </div>
                </div>

                <div className="mt-3 text-sm text-gray-600">
                  {loading ? "Lade…" : `${filteredItems.length} Einträge`}
                </div>
              </div>

              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">Datum</th>
                      <th className="text-left font-medium px-4 py-3">
                        Mandant
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Rechnung
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Schuldner
                      </th>
                      <th className="text-left font-medium px-4 py-3">Firma</th>
                      <th className="text-left font-medium px-4 py-3">
                        Betrag
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Fällig
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Mahnstufe
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Status
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Export
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Aktion
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {filteredItems.map((x) => {
                      const created = x?.created_at || x?.submitted_at;
                      const tenant =
                        x?.company_id || x?.space_name || x?.space_id || "—";
                      const invoiceTitle =
                        x?.invoice_number || x?.payload?.invoice_number || "—";
                      const invoiceTechId = x?.invoice_id || x?.id || "—";
                      const debtor =
                        x?.debtor_name ||
                        x?.debtor_contact_name ||
                        x?.payload?.address?.name ||
                        "—";
                      const firma =
                        x?.debtor_company ||
                        x?.payload?.address?.company ||
                        "—";
                      const amount = safeNum(x?.amount);
                      const currency = x?.currency || "EUR";
                      const due = x?.due_date || x?.payload?.due_date || "—";
                      const level =
                        x?.cobot_reminder_level !== null &&
                        x?.cobot_reminder_level !== undefined
                          ? String(x.cobot_reminder_level)
                          : "—";
                      const status = getDisplayStatus(x);
                      const cid = x?.case_id || "";
                      const accepting = busyCaseId === cid;

                      return (
                        <tr
                          key={cid || `${tenant}-${invoiceTechId}-${created}`}
                          className="hover:bg-gray-50 align-top"
                        >
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {formatDate(created)}
                          </td>

                          <td className="px-4 py-3 min-w-[180px]">
                            <div className="font-medium text-gray-900 break-words">
                              {safeStr(tenant)}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {safeStr(x?.space_id)}
                            </div>
                          </td>

                          <td className="px-4 py-3 min-w-[260px]">
                            <div className="font-medium text-gray-900 break-words">
                              {safeStr(invoiceTitle)}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {safeStr(invoiceTechId)}
                            </div>
                          </td>

                          <td className="px-4 py-3 text-gray-900 min-w-[160px] break-words">
                            {safeStr(debtor)}
                          </td>

                          <td className="px-4 py-3 text-gray-900 min-w-[160px] break-words">
                            {safeStr(firma)}
                          </td>

                          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                            {amount !== null
                              ? `${amount.toFixed(2)} ${currency}`
                              : safeStr(x?.amount)}
                          </td>

                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {safeStr(due)}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge
                              kind={
                                level !== "—" && Number(level) >= 2
                                  ? "yellow"
                                  : "default"
                              }
                            >
                              {level}
                            </Badge>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge kind={statusKind(status)}>{status}</Badge>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {x?.exported_at ? (
                              <div className="text-xs text-gray-600">
                                <div className="font-medium text-blue-700">
                                  Ja
                                </div>
                                <div>{formatDate(x.exported_at)}</div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Nein</span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {status === "accepted" ? (
                              <Button
                                variant="success"
                                disabled
                                title="Bereits angenommen"
                              >
                                Angenommen ✅
                              </Button>
                            ) : status === "exported" ? (
                              <Button
                                variant="secondary"
                                disabled
                                title="Bereits exportiert"
                              >
                                Exportiert 📦
                              </Button>
                            ) : (
                              <Button
                                variant="primary"
                                onClick={() => acceptCase(cid)}
                                disabled={!cid || accepting}
                                title="Diesen Fall annehmen"
                              >
                                {accepting ? "…" : "Annehmen"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {!loading && filteredItems.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-10 text-center text-gray-500"
                          colSpan={11}
                        >
                          Keine Einträge gefunden.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : view === "tenants" ? (
            <>
              <div className="p-4 border-b border-gray-100">
                <div className="text-sm text-gray-600">
                  {tenantsLoading
                    ? "Lade Mandanten…"
                    : `${tenants.length} Mandanten`}
                </div>
              </div>

              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">
                        Mandant
                      </th>
                      <th className="text-left font-medium px-4 py-3">Firma</th>
                      <th className="text-left font-medium px-4 py-3">
                        Adresse
                      </th>
                      <th className="text-left font-medium px-4 py-3">
                        Kontakt
                      </th>
                      <th className="text-left font-medium px-4 py-3">IBAN</th>
                      <th className="text-left font-medium px-4 py-3">AGB</th>
                      <th className="text-left font-medium px-4 py-3">Profil</th>
                      <th className="text-left font-medium px-4 py-3">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {tenants.map((tenant) => {
                      const address = formatAddress(tenant);
                      const isComplete = profileComplete(tenant);
                      const copyKey =
                        tenant?.company_id ||
                        tenant?.space_id ||
                        tenant?.space_name ||
                        "";

                      return (
                        <tr
                          key={
                            tenant?.company_id ||
                            tenant?.space_id ||
                            tenant?.space_name
                          }
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 min-w-[180px]">
                            <div className="font-medium text-gray-900 break-words">
                              {safeStr(
                                tenant?.company_name ||
                                  tenant?.legal_name ||
                                  tenant?.space_name ||
                                  tenant?.company_id
                              )}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {safeStr(tenant?.company_id || tenant?.space_id)}
                            </div>
                          </td>

                          <td className="px-4 py-3 min-w-[180px]">
                            <div className="font-medium text-gray-900 break-words">
                              {safeStr(
                                tenant?.legal_name || tenant?.company_name
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {safeStr(tenant?.legal_form)}
                            </div>
                          </td>

                          <td className="px-4 py-3 min-w-[220px]">
                            <div className="text-gray-900 break-words">
                              {address.line1}
                            </div>
                            <div className="text-xs text-gray-500 break-words">
                              {address.line2}
                            </div>
                          </td>

                          <td className="px-4 py-3 min-w-[180px]">
                            <div className="text-gray-900 break-words">
                              {safeStr(tenant?.contact_person)}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {safeStr(tenant?.contact_email)}
                            </div>
                          </td>

                          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span title={tenant?.iban || ""}>
                                {formatIbanShort(tenant?.iban)}
                              </span>

                              {tenant?.iban ? (
                                <button
                                  type="button"
                                  onClick={() => copyIban(tenant)}
                                  className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                                  title="Vollständige IBAN kopieren"
                                >
                                  {copiedIbanFor === copyKey
                                    ? "Kopiert"
                                    : "Kopieren"}
                                </button>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {tenant?.inkasso_agb_accepted ? (
                              <Badge kind="green">Akzeptiert</Badge>
                            ) : (
                              <Badge kind="yellow">Fehlt</Badge>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {isComplete ? (
                              <Badge kind="green">Vollständig</Badge>
                            ) : (
                              <Badge kind="yellow">Unvollständig</Badge>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge
                              kind={
                                tenant?.status === "blocked" ? "red" : "default"
                              }
                            >
                              {tenant?.status || "active"}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}

                    {!tenantsLoading && tenants.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-10 text-center text-gray-500"
                          colSpan={8}
                        >
                          Keine Mandanten gefunden.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="p-6 border-b border-gray-100">
                <div className="text-lg font-semibold text-gray-900">
                  Mandanten → Aktenimport
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  CSV-Datei aus IKAROS hochladen. Der sichtbare Altbestand des
                  Mandanten wird dabei überschrieben.
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                    <div className="text-sm font-semibold text-gray-900 mb-4">
                      Import starten
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mandant auswählen
                        </label>
                        <select
                          value={selectedCompanyId}
                          onChange={(e) => setSelectedCompanyId(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white"
                        >
                          <option value="">Bitte wählen</option>
                          {tenants.map((tenant) => {
                            const value =
                              tenant?.company_id ||
                              tenant?.space_id ||
                              tenant?.company_name ||
                              "";
                            const label =
                              tenant?.company_name ||
                              tenant?.legal_name ||
                              tenant?.space_name ||
                              tenant?.company_id ||
                              "Unbekannt";

                            return (
                              <option key={value} value={value}>
                                {label} ({value})
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Datei auswählen
                        </label>
                        <input
                          id="mandant-case-import-file"
                          type="file"
                          accept=".csv,text/csv"
                          onChange={(e) =>
                            setSelectedFile(e.target.files?.[0] || null)
                          }
                          className="block w-full text-sm text-gray-700"
                        />
                        <div className="mt-2 text-xs text-gray-500">
                          Erwartet wird eine CSV-Datei für den ausgewählten
                          Mandanten.
                        </div>
                      </div>

                      {selectedFile ? (
                        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                          Datei: <b>{selectedFile.name}</b>
                        </div>
                      ) : null}

                      <div className="flex gap-3 flex-wrap">
                        <Button
                          variant="primary"
                          onClick={handleImport}
                          disabled={
                            importLoading ||
                            deleteLoading ||
                            !selectedCompanyId ||
                            !selectedFile
                          }
                        >
                          {importLoading ? "Import läuft…" : "Import starten"}
                        </Button>

                        <Button
                          variant="danger"
                          onClick={handleDeleteCases}
                          disabled={importLoading || deleteLoading || !selectedCompanyId}
                        >
                          {deleteLoading ? "Bestand wird gelöscht…" : "Bestand löschen"}
                        </Button>

                        <Button
                          variant="secondary"
                          onClick={resetImport}
                          disabled={importLoading || deleteLoading}
                        >
                          Zurücksetzen
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-5 bg-white">
                    <div className="text-sm font-semibold text-gray-900 mb-4">
                      Import Ergebnis
                    </div>

                    {!importStats ? (
                      <div className="text-sm text-gray-500">
                        Noch kein Import durchgeführt.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Vorgang Datum
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {formatDate(importStats.importedAt)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Mandant
                            </div>
                            <div className="mt-1 font-semibold text-gray-900 break-all">
                              {safeStr(importStats.companyId)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Gelieferte Datensätze
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {safeStr(importStats.receivedCases)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Importierte Akten
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {safeStr(importStats.importedCases)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Überschriebene Altakten
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {safeStr(importStats.deletedPreviousCases)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Übersprungene geschlossene Akten
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {safeStr(importStats.skippedClosedCases)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">
                              Summe offene HF
                            </div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {formatCurrency(importStats.totalOpenAmount, "EUR")}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 p-4">
                            <div className="text-xs text-gray-500">Summe Gesamt</div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {formatCurrency(importStats.totalAmount, "EUR")}
                            </div>
                          </div>
                        </div>

                        <div
                          className={`rounded-xl p-4 text-sm ${
                            importStats.success
                              ? "border border-green-200 bg-green-50 text-green-800"
                              : "border border-yellow-200 bg-yellow-50 text-yellow-800"
                          }`}
                        >
                          {importStats.action === "delete"
                            ? `Bestand erfolgreich gelöscht. Entfernte Akten: ${safeStr(
                                importStats.deletedPreviousCases
                              )}.`
                            : importStats.success
                            ? "Import erfolgreich abgeschlossen."
                            : "Import abgeschlossen, bitte Ergebnis prüfen."}
                        </div>

                        <pre className="overflow-auto rounded-xl bg-gray-900 text-gray-100 text-xs p-4">
{JSON.stringify(importResult, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="p-4 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between flex-wrap gap-2">
            <div>
              {view === "cases" ? (
                <>
                  Workflow: <b>New → Annehmen → Export</b>. Exportierte Fälle
                  bleiben sichtbar und werden über <b>exported_at</b> als
                  „Exported“ gekennzeichnet.
                </>
              ) : view === "tenants" ? (
                <>
                  Mandanten-Übersicht für Admin: Stammdaten, Adresse, Kontakt,
                  IBAN, AGB-Status und Profil-Vollständigkeit.
                </>
              ) : (
                <>
                  Import-Workflow: <b>Mandant wählen → CSV hochladen → Bestand
                  überschreiben</b>. Abgelegte Akten werden aktuell nicht
                  angezeigt.
                </>
              )}
            </div>

            {view === "cases" ? (
              <div className="flex items-center gap-2">
                <Badge kind="yellow">new</Badge>
                <Badge kind="green">accepted</Badge>
                <Badge kind="blue">exported</Badge>
              </div>
            ) : view === "mandantImport" ? (
              <div className="flex items-center gap-2">
                <Badge kind="yellow">CSV Import</Badge>
                <Badge kind="red">Bestand löschen</Badge>
                <Badge kind="blue">Überschreibt Bestand</Badge>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}